import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatQAR } from "../lib/money";
import { ExpenseForm } from "../components/ExpensePanel";
export function CustomerDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [expenseCaseId, setExpenseCaseId] = useState("");
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: "", contactPerson: "", mobile: "", address: "" });
    function load() {
        if (!id)
            return;
        api
            .get(`/customers/${id}`)
            .then((d) => {
            setData(d);
            setExpenseCaseId((cur) => (cur && d.cases.some((c) => c.id === cur) ? cur : d.cases[0]?.id ?? ""));
            setForm({
                name: d.customer.name,
                contactPerson: d.customer.contactPerson ?? "",
                mobile: d.customer.mobile ?? "",
                address: d.customer.address ?? "",
            });
        })
            .catch(() => setError("Couldn't load this customer."));
    }
    useEffect(load, [id]);
    async function save() {
        if (!id || !form.name.trim())
            return;
        setSaving(true);
        setError(null);
        try {
            await api.put(`/customers/${id}`, {
                name: form.name.trim(),
                contactPerson: form.contactPerson.trim() || undefined,
                mobile: form.mobile.trim() || undefined,
                address: form.address.trim() || undefined,
            });
            setEditing(false);
            load();
        }
        catch (e) {
            setError(e?.code === "forbidden" ? "You don't have permission to edit customers." : "Couldn't save changes.");
        }
        finally {
            setSaving(false);
        }
    }
    if (error && !data)
        return <div style={{ color: "var(--st-risk)" }}>{error}</div>;
    if (!data)
        return <div style={{ color: "var(--ink-faint)" }}>Loading…</div>;
    const totals = data.cases.reduce((acc, c) => {
        acc.receivable += c.financialSummary.totalRecoverable;
        acc.recovered += c.financialSummary.amountReceived;
        acc.outstanding += c.financialSummary.outstandingBalance;
        return acc;
    }, { receivable: 0, recovered: 0, outstanding: 0 });
    return (<div>
      <div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: "20px 22px", marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          {!editing ? (<div>
              <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.4rem", margin: 0 }}>{data.customer.name}</h1>
              <div style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginTop: 4 }}>
                {data.customer.customerCode ?? "—"} {data.customer.mobile ? `· ${data.customer.mobile}` : ""}
                {data.customer.contactPerson ? ` · ${data.customer.contactPerson}` : ""}
              </div>
              {data.customer.address && <div style={{ fontSize: "0.82rem", color: "var(--ink-faint)", marginTop: 2 }}>{data.customer.address}</div>}
            </div>) : (<div className="lg-stack" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flex: 1, marginRight: 16 }}>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" style={inputStyle}/>
              <input value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} placeholder="Contact person" style={inputStyle}/>
              <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile" style={inputStyle}/>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" style={inputStyle}/>
            </div>)}
          {!editing ? (<button onClick={() => setEditing(true)} style={linkBtn}>Edit</button>) : (<div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditing(false)} style={linkBtn}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnStyle}>Save</button>
            </div>)}
        </div>
        {error && <div style={{ color: "var(--st-risk)", fontSize: "0.82rem", marginTop: 8 }}>{error}</div>}
        <div className="lg-pair" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line-soft)" }}>
          <Stat label="Total Cases" value={String(data.cases.length)}/>
          <Stat label="Total Receivable" value={formatQAR(totals.receivable)}/>
          <Stat label="Total Recovered" value={formatQAR(totals.recovered)} color="var(--st-open)"/>
          <Stat label="Outstanding" value={formatQAR(totals.outstanding)} color="var(--st-risk)"/>
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line-soft)", fontWeight: 700, fontSize: "0.88rem" }}>Case History</div>
        {data.cases.map((c) => (<Link key={c.id} to={`/legal-cases/cases/${c.id}`} style={{ display: "flex", justifyContent: "space-between", padding: "10px 18px", borderTop: "1px solid var(--line-soft)", textDecoration: "none", color: "inherit" }}>
            <span className="mono" style={{ color: "var(--accent-ink)", fontWeight: 600, fontSize: "0.82rem" }}>{c.caseCode}</span>
            <span style={{ fontSize: "0.82rem", color: "var(--ink-soft)" }}>{c.legalStage.replace(/_/g, " ")}</span>
            <span className="mono tabnum" style={{ fontSize: "0.82rem" }}>{formatQAR(c.financialSummary.outstandingBalance)}</span>
          </Link>))}
        {data.cases.length === 0 && <div style={{ padding: 20, color: "var(--ink-faint)", fontSize: "0.85rem" }}>No cases for this customer yet.</div>}
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, marginTop: 18 }}>
        <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line-soft)", display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "0.88rem" }}>
          <span>Additional Expenses</span>
          <span className="mono tabnum" style={{ fontWeight: 600 }}>{formatQAR(data.expenses.reduce((s, e) => s + e.amount, 0))}</span>
        </div>
        {data.cases.length > 0 && (<div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line-soft)" }}>
            {data.cases.length > 1 && (<select value={expenseCaseId} onChange={(e) => setExpenseCaseId(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }}>
                {data.cases.map((c) => (<option key={c.id} value={c.id}>{c.caseCode}</option>))}
              </select>)}
            {expenseCaseId && <ExpenseForm key={expenseCaseId} caseId={expenseCaseId} onSaved={load}/>}
          </div>)}
        {data.expenses.map((e) => (<Link key={e.id} to={`/legal-cases/cases/${e.caseId}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 18px", borderTop: "1px solid var(--line-soft)", textDecoration: "none", color: "inherit", fontSize: "0.82rem" }}>
            <span className="mono" style={{ color: "var(--accent-ink)", fontWeight: 600 }}>{e.caseCode}</span>
            <span style={{ flex: 1, color: "var(--ink-soft)" }}>{e.expenseType}{e.description ? ` — ${e.description}` : ""} · {new Date(e.expenseDate).toLocaleDateString()}</span>
            <span className="mono tabnum">{formatQAR(e.amount)}</span>
          </Link>))}
        {data.expenses.length === 0 && <div style={{ padding: 20, color: "var(--ink-faint)", fontSize: "0.85rem" }}>No additional expenses recorded.</div>}
      </div>
    </div>);
}
function Stat({ label, value, color }) {
    return (<div>
      <div className="mono" style={{ fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>{label}</div>
      <div className="mono tabnum" style={{ fontSize: "1.05rem", fontWeight: 600, marginTop: 3, color: color ?? "var(--ink)" }}>{value}</div>
    </div>);
}
const inputStyle = { border: "1px solid var(--line)", borderRadius: 8, padding: "7px 10px", fontSize: "0.82rem", fontFamily: "inherit" };
const btnStyle = { background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.8rem", padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer" };
const linkBtn = { border: "1px solid var(--line)", background: "var(--surface)", color: "var(--ink-soft)", fontWeight: 600, fontSize: "0.78rem", padding: "6px 12px", borderRadius: 7, cursor: "pointer" };
